import type OutlinePipelinePlugin from "phaser3-rex-plugins/plugins/outlinepipeline-plugin.js";
import type { Unsubscriber, Writable } from "svelte/store";
import { get, writable } from "svelte/store";
import type CancelablePromise from "cancelable-promise";
import { Deferred } from "ts-deferred";
import type { AvailabilityStatus as AvailabilityStatusType } from "@workadventure/messages";
import { SayMessageType, AvailabilityStatus, PositionMessage_Direction } from "@workadventure/messages";
import { defaultWoka } from "@workadventure/shared-utils";
import { currentPlayerWokaStore } from "../../Stores/CurrentPlayerWokaStore";
import { currentPlayerCompanionStore } from "../../Stores/CurrentPlayerCompanionStore";
import { PlayerStatusDot } from "../Components/PlayerStatusDot";
import { TalkIcon } from "../Components/TalkIcon";
import type { OutlineableInterface } from "../Game/OutlineableInterface";
import { createColorStore } from "../../Stores/OutlineColorStore";
import type { PictureStore } from "../../Stores/PictureStore";
import { TexturesHelper } from "../Helpers/TexturesHelper";
import { DEPTH_INGAME_TEXT_INDEX } from "../Game/DepthIndexes";
import type { GameScene } from "../Game/GameScene";
import { Companion } from "../Companion/Companion";
import { CharacterTextureError } from "../../Exception/CharacterTextureError";
import { getPlayerAnimations, PlayerAnimationTypes } from "../Player/Animation";
import { ProtobufClientUtils } from "../../Network/ProtobufClientUtils";
import { SpeakerIcon } from "../Components/SpeakerIcon";
import { MegaphoneIcon } from "../Components/MegaphoneIcon";
import { StringUtils } from "../../Utils/StringUtils";

import { lazyLoadPlayerCharacterTextures } from "./PlayerTexturesLoadingManager";
import { SpeechBubble } from "./SpeechBubble";
import { SpeechDomElement } from "./SpeechDomElement";
import { ThinkingCloud } from "./ThinkingCloud";
import Text = Phaser.GameObjects.Text;
import Container = Phaser.GameObjects.Container;
import Sprite = Phaser.GameObjects.Sprite;
import DOMElement = Phaser.GameObjects.DOMElement;
import RenderTexture = Phaser.GameObjects.RenderTexture;

const playerNameY = -25;
const interactiveRadius = 25;
const NAME_LABEL_ZOOM_AWARE_SCALING = true;
const PLAYER_NAME_FONT_SIZE_LATIN = "8px";
const PLAYER_NAME_FONT_SIZE_NON_LATIN = "10px";
// World-space clamp for text object scale (actual visible size also depends on camera zoom).
// Keep low enough so super-zoom-in compensation can still shrink labels when needed.
const NAME_LABEL_SCALE_MIN = 0.25;
const NAME_LABEL_SCALE_MAX = 1.8;
// Desired on-screen size multiplier (relative to the default label size).
// We only boost labels when zoomed out; zoomed-in stays at baseline size.
const NAME_LABEL_SCREEN_SCALE_BASE = 1.0;
const NAME_LABEL_SCREEN_SCALE_MAX = 1.1; // zoomed-out
// For super zoom-in, reduce label growth only slightly vs default world scaling.
const NAME_LABEL_ZOOM_IN_WORLD_SCALE_MIN = 0.9;
const NAME_LABEL_ZOOM_IN_SHRINK_START = 1.25;
const NAME_LABEL_ZOOM_IN_SHRINK_RANGE = 1.75;
const NAME_LABEL_SCALE_STEP = 0.01;
const NAME_LABEL_SCALE_ZOOM_EXPONENT = 0.65;
const NAME_LABEL_ZOOM_IN_SHRINK_EXPONENT = 1.6;

export const CHARACTER_BODY_WIDTH = 16;
export const CHARACTER_BODY_HEIGHT = 16;
export const CHARACTER_BODY_OFFSET_X = 0;
export const CHARACTER_BODY_OFFSET_Y = 8;

export abstract class Character extends Container implements OutlineableInterface {
    private bubble: RenderTexture | null | DOMElement = null;
    private playerNameText: Text | undefined;
    private readonly talkIcon: TalkIcon;
    protected readonly statusDot: PlayerStatusDot;
    protected readonly speakerIcon: SpeakerIcon;
    protected readonly megaphoneIcon: MegaphoneIcon;
    public playerName: string;
    public sprites: Map<string, Sprite>;
    protected _lastDirection: PositionMessage_Direction = PositionMessage_Direction.DOWN;
    //private teleportation: Sprite;
    private invisible: boolean;
    private clickable: boolean;
    public companion?: Companion;
    private companionPictureUnsubscribe: Unsubscriber | undefined;
    private emote: Phaser.GameObjects.DOMElement | null = null;
    private emoteTween: Phaser.Tweens.Tween | null = null;
    private texts: Map<string, Phaser.GameObjects.DOMElement> = new Map();
    private textsToBuild = new Map();
    scene: GameScene;
    private lastRenderedSprite: string | undefined;
    private readonly isCurrentPlayer: boolean;
    private readonly _pictureStore: Writable<string | undefined>;
    protected readonly outlineColorStore = createColorStore();
    private outlineColorStoreUnsubscribe: Unsubscriber | undefined;
    private texturePromise: CancelablePromise<string[] | void> | undefined;
    private destroyed = false;
    private playerNameVisualScale = 1;
    private playerLabelStackVisible = true;

    /**
     * A deferred promise that resolves when the texture of the character is actually displayed.
     */
    private textureLoadedDeferred = new Deferred<void>();

    constructor(
        scene: GameScene,
        x: number,
        y: number,
        texturesPromise: CancelablePromise<string[]>,
        name: string,
        direction: PositionMessage_Direction,
        moving: boolean,
        frame: string | number,
        isClickable: boolean,
        companionTexturePromise: CancelablePromise<string>,
        userId?: string | null
    ) {
        super(scene, x, y /*, texture, frame*/);
        this.scene = scene;
        this.playerName = name;
        this.invisible = true;
        this.clickable = false;

        this.sprites = new Map<string, Sprite>();

        // Note: the picture store is rarely used because most of the time, we display the character sent in the space
        // by the remote player.
        // The sole place where we need the picture store is when you click on a Woka on the map and want to display
        // the Woka sprite in the popup that opens.
        this._pictureStore = writable<string | undefined>(undefined);
        this.waitAndGetSnapshot()
            .then((htmlImageElementSrc) => {
                this._pictureStore.set(htmlImageElementSrc);
            })
            .catch((e) => {
                console.warn(e);
                this._pictureStore.set(defaultWoka);
            });

        this.isCurrentPlayer = userId != undefined;
        if (this.isCurrentPlayer) {
            this.waitAndGetSnapshot()
                .then((htmlImageElementSrc) => {
                    currentPlayerWokaStore.set(htmlImageElementSrc);
                })
                .catch((e) => {
                    console.warn(e);
                    currentPlayerWokaStore.set(defaultWoka);
                });
        }

        //textures are inside a Promise in case they need to be lazyloaded before use.
        this.texturePromise = texturesPromise
            .then((textures) => {
                this.addTextures(textures, frame);
                this.invisible = false;
                this.playAnimation(direction, moving);
                this.textureLoadedDeferred.resolve();
            })
            .catch(() => {
                return lazyLoadPlayerCharacterTextures(scene.superLoad, [
                    {
                        id: "color_22",
                        url: "resources/customisation/character_color/character_color21.png",
                    },
                    {
                        id: "eyes_23",
                        url: "resources/customisation/character_eyes/character_eyes23.png",
                    },
                ])
                    .then((textures) => {
                        this.addTextures(textures, frame);
                        this.invisible = false;
                        this.playAnimation(direction, moving);
                        this.textureLoadedDeferred.resolve();
                    })
                    .catch((e) => {
                        this.textureLoadedDeferred.reject(e);
                        throw e;
                    });
            })
            .finally(() => {
                this.texturePromise = undefined;
            });

        if (typeof companionTexturePromise !== "undefined") {
            this.addCompanion(companionTexturePromise);
        }

        // We delay the assignment of the text because it can take up to 16ms.
        // If we enter a zone with 100 people, that is a 1.6s freeze.
        // requestAnimationFrame has the priority over setTimeout so the game will keep running smoothly.
        setTimeout(() => {
            if (this.destroyed) {
                // The character has been destroyed before the timeout
                return;
            }

            // Todo: Replace the font family with a better one
            // Use larger font size for non-Latin characters (Arabic, CJK, etc.) for better readability
            const fontSize = StringUtils.containsNonLatinCharacters(name)
                ? PLAYER_NAME_FONT_SIZE_NON_LATIN
                : PLAYER_NAME_FONT_SIZE_LATIN;
            this.playerNameText = new Text(scene, 0, playerNameY, name, {
                fontFamily: '"Press Start 2P"',
                fontSize,
                strokeThickness: 2,
                stroke: "#14304C",
                metrics: {
                    ascent: 20,
                    descent: 10,
                    fontSize: 35,
                },
            });

            this.playerNameText.setOrigin(0.5).setDepth(DEPTH_INGAME_TEXT_INDEX);
            this.playerNameText.setVisible(this.playerLabelStackVisible);
            this.add([this.playerNameText]);
            this.applyPlayerNameScaleForCurrentSceneZoom();

            // Reposition status dot and megaphone icon
            this.refreshPlayerNameAccessoriesPosition();
            this.statusDot.visible = this.playerLabelStackVisible;
            this.megaphoneIcon.visible = this.playerLabelStackVisible;

            scene.getOutlineManager().add(this.playerNameText, () => {
                return this.getCurrentOutline();
            });

            this.outlineColorStoreUnsubscribe = this.outlineColorStore.subscribe((color) => {
                this.setOutline(color);
            });
        }, 0);

        this.statusDot = new PlayerStatusDot(scene, 0, playerNameY - 1);
        this.megaphoneIcon = new MegaphoneIcon(scene, 0, playerNameY - 1);
        this.statusDot.visible = false;
        this.megaphoneIcon.visible = false;
        this.talkIcon = new TalkIcon(scene, 0, -45);
        this.speakerIcon = new SpeakerIcon(scene, 0, -45);
        this.add([this.talkIcon, this.speakerIcon, this.statusDot, this.megaphoneIcon]);

        if (isClickable) {
            this.setInteractive({
                hitArea: new Phaser.Geom.Circle(8, 8, interactiveRadius),
                hitAreaCallback: Phaser.Geom.Circle.Contains, //eslint-disable-line @typescript-eslint/unbound-method
                useHandCursor: true,
            });
        }

        this.setClickable(isClickable);

        scene.add.existing(this);

        this.scene.physics.world.enableBody(this);
        this.getBody().setImmovable(true);
        this.getBody().setCollideWorldBounds(true);
        this.setSize(CHARACTER_BODY_WIDTH, CHARACTER_BODY_HEIGHT);
        this.getBody().setSize(CHARACTER_BODY_WIDTH, CHARACTER_BODY_HEIGHT); //edit the hitbox to better match the character model
        this.getBody().setOffset(CHARACTER_BODY_OFFSET_X, CHARACTER_BODY_OFFSET_Y);
        this.setDepth(this.y + 16);
    }

    public updatePlayerName(name: string): void {
        this.playerName = name;
        if (this.playerNameText) {
            const fontSize = StringUtils.containsNonLatinCharacters(name)
                ? PLAYER_NAME_FONT_SIZE_NON_LATIN
                : PLAYER_NAME_FONT_SIZE_LATIN;
            this.playerNameText.setText(name);
            this.playerNameText.setFontSize(fontSize);
            this.applyPlayerNameVisualScale(this.playerNameVisualScale);
        }
    }

    public updatePlayerNameScaleForZoom(cameraZoom: number): void {
        if (!NAME_LABEL_ZOOM_AWARE_SCALING) {
            return;
        }
        this.applyPlayerNameVisualScale(this.computePlayerNameVisualScale(cameraZoom));
    }

    public updatePlayerNameScaleForDiscreteLevel(cameraZoom: number, normalizedDiscreteLevel: number): void {
        if (!NAME_LABEL_ZOOM_AWARE_SCALING) {
            return;
        }
        this.applyPlayerNameVisualScale(
            this.computePlayerNameVisualScaleForDiscreteLevel(cameraZoom, normalizedDiscreteLevel)
        );
    }

    public updateTextures(textures: string[], frame?: string | number): void {
        for (const sprite of this.sprites.values()) {
            this.remove(sprite);
            sprite.destroy();
        }
        this.sprites.clear();
        this.lastRenderedSprite = undefined;
        this.addTextures(textures, frame);
        this.invisible = false;
        this.playAnimation(this._lastDirection, false);
        if (this.isCurrentPlayer) {
            this.getSnapshot()
                .then((htmlImageElementSrc) => {
                    this.lastRenderedSprite = htmlImageElementSrc;
                    currentPlayerWokaStore.set(htmlImageElementSrc);
                    this._pictureStore.set(htmlImageElementSrc);
                })
                .catch((e) => {
                    console.warn(e);
                    this.lastRenderedSprite = defaultWoka;
                    currentPlayerWokaStore.set(defaultWoka);
                    this._pictureStore.set(defaultWoka);
                });
        } else {
            this.getSnapshot()
                .then((htmlImageElementSrc) => {
                    this.lastRenderedSprite = htmlImageElementSrc;
                    this._pictureStore.set(htmlImageElementSrc);
                })
                .catch((e) => {
                    console.warn(e);
                    this.lastRenderedSprite = defaultWoka;
                    this._pictureStore.set(defaultWoka);
                });
        }
    }

    public updateCompanionTexture(texturePromise: CancelablePromise<string> | null): void {
        if (this.companionPictureUnsubscribe) {
            this.companionPictureUnsubscribe();
            this.companionPictureUnsubscribe = undefined;
        }
        if (this.companion) {
            this.companion.destroy();
            this.companion = undefined;
        }
        if (this.isCurrentPlayer) {
            currentPlayerCompanionStore.set(undefined);
        }
        if (texturePromise) {
            this.addCompanion(texturePromise);
        }
    }

    private waitAndGetSnapshot(): Promise<string> {
        if (this.lastRenderedSprite) {
            return Promise.resolve(this.lastRenderedSprite);
        }
        return new Promise((resolve) => {
            // getSnapshot can be quite long (~16ms) so we delay it to avoid freezing the game.
            // Since requestAnimationFrame has the priority over setTimeout, the game will keep running smoothly.
            this.textureLoadedDeferred.promise
                .then(() => {
                    setTimeout(() => {
                        this.getSnapshot()
                            .then((htmlImageElementSrc) => {
                                this.lastRenderedSprite = htmlImageElementSrc;
                                resolve(htmlImageElementSrc);
                            })
                            .catch((e) => {
                                console.warn(e);
                                this.lastRenderedSprite = defaultWoka;
                                resolve(defaultWoka);
                            });
                    }, 0);
                })
                .catch((e) => {
                    console.warn(e);
                    this.lastRenderedSprite = defaultWoka;
                    resolve(defaultWoka);
                });
        });
    }

    public setClickable(clickable = true): void {
        if (this.clickable === clickable) {
            return;
        }
        this.clickable = clickable;
        if (clickable) {
            this.setInteractive({
                hitArea: new Phaser.Geom.Circle(8, 8, interactiveRadius),
                hitAreaCallback: Phaser.Geom.Circle.Contains, //eslint-disable-line @typescript-eslint/unbound-method
                useHandCursor: true,
            });
            return;
        }
        this.disableInteractive();
    }

    public isClickable() {
        return this.clickable;
    }

    public getPosition(): { x: number; y: number } {
        return { x: this.x, y: this.y };
    }

    /**
     * Returns position based on where player is currently facing
     * @param shift How far from player should the point of interest be.
     */
    public getDirectionalActivationPosition(shift: number): { x: number; y: number } {
        switch (this._lastDirection) {
            case PositionMessage_Direction.DOWN: {
                return { x: this.x, y: this.y + shift };
            }
            case PositionMessage_Direction.LEFT: {
                return { x: this.x - shift, y: this.y };
            }
            case PositionMessage_Direction.RIGHT: {
                return { x: this.x + shift, y: this.y };
            }
            case PositionMessage_Direction.UP: {
                return { x: this.x, y: this.y - shift };
            }
            case PositionMessage_Direction.UNRECOGNIZED: {
                return { x: this.x, y: this.y };
            }
        }
    }

    private async getSnapshot(): Promise<string> {
        const sprites = Array.from(this.sprites.values()).map((sprite) => {
            return { sprite, frame: 1 };
        });
        return TexturesHelper.getSnapshot(this.scene, ...sprites).catch((reason) => {
            console.warn(reason);
            for (const sprite of this.sprites.values()) {
                // we can be sure that either predefined woka or body texture is at this point loaded
                if (sprite.texture.key.includes("color") || sprite.texture.key.includes("male")) {
                    return this.scene.textures.getBase64(sprite.texture.key);
                }
            }
            return "male1";
        });
    }

    public toggleTalk(show = true, forceClose = false): void {
        if (this.getAvailabilityStatus() === AvailabilityStatus.SPEAKER) {
            this.talkIcon.show(false, forceClose);
            this.speakerIcon.show(show, forceClose);
        } else {
            this.talkIcon.show(show, forceClose);
            this.speakerIcon.show(false, forceClose);
        }
    }

    public setAvailabilityStatus(availabilityStatus: AvailabilityStatusType, instant = false): void {
        this.statusDot.setAvailabilityStatus(availabilityStatus, instant);
        if (this.getAvailabilityStatus() === AvailabilityStatus.SPEAKER) {
            this.megaphoneIcon.show(true, false);
        } else {
            this.megaphoneIcon.show(false, false);
        }
    }

    public getAvailabilityStatus() {
        return this.statusDot.availabilityStatus;
    }

    public isMegaphoneIconShown(): boolean {
        return this.megaphoneIcon.isShown();
    }

    public getPlayerLabelAnchorWorldPosition(): { x: number; y: number } {
        return { x: this.x, y: this.y + playerNameY };
    }

    public setPlayerLabelStackVisible(visible: boolean): void {
        if (this.playerLabelStackVisible === visible) {
            return;
        }

        this.playerLabelStackVisible = visible;
        this.playerNameText?.setVisible(visible);
        this.statusDot.setVisible(visible);
        this.megaphoneIcon.setVisible(visible);
    }

    public addCompanion(texturePromise: CancelablePromise<string>): void {
        this.companion = new Companion(this.scene, this.x, this.y, texturePromise);
        if (this.isCurrentPlayer) {
            this.companionPictureUnsubscribe = this.companion.pictureStore.subscribe((src) => {
                currentPlayerCompanionStore.set(src);
            });
        }
    }

    private addTextures(textures: string[], frame?: string | number): void {
        if (textures.length < 1) {
            throw new CharacterTextureError("No texture given");
        }

        for (const texture of textures) {
            if (this.scene && !this.scene.textures.exists(texture)) {
                throw new CharacterTextureError(`Texture "${texture}" not found`);
            }
            const sprite = new Sprite(this.scene, 0, 0, texture, frame);
            this.add(sprite);
            getPlayerAnimations(texture).forEach((d) => {
                if (!this.scene.anims.exists(d.key)) {
                    this.scene.anims.create({
                        key: d.key,
                        frames: this.scene.anims.generateFrameNumbers(d.frameModel, { frames: d.frames }),
                        frameRate: d.frameRate,
                        repeat: d.repeat,
                    });
                }
            });
            // Needed, otherwise, animations are not handled correctly.
            if (this.scene) {
                this.scene.sys.updateList.add(sprite);
            }
            this.sprites.set(texture, sprite);
        }
    }

    private getOutlinePlugin(): OutlinePipelinePlugin | undefined {
        return this.scene.plugins.get("rexOutlinePipeline") as unknown as OutlinePipelinePlugin | undefined;
    }

    private computePlayerNameVisualScale(cameraZoom: number): number {
        if (!NAME_LABEL_ZOOM_AWARE_SCALING) {
            return 1;
        }

        const scaleZoom = this.scene.scale.zoom || 1;
        const effectiveZoom = Math.max(cameraZoom * scaleZoom, 0.0001);
        const raw = 1 / Math.pow(effectiveZoom, NAME_LABEL_SCALE_ZOOM_EXPONENT);
        const clamped = Phaser.Math.Clamp(raw, NAME_LABEL_SCALE_MIN, NAME_LABEL_SCALE_MAX);
        const quantized = Math.round(clamped / NAME_LABEL_SCALE_STEP) * NAME_LABEL_SCALE_STEP;
        return Phaser.Math.Clamp(quantized, NAME_LABEL_SCALE_MIN, NAME_LABEL_SCALE_MAX);
    }

    private computePlayerNameVisualScaleForDiscreteLevel(cameraZoom: number, normalizedDiscreteLevel: number): number {
        if (!NAME_LABEL_ZOOM_AWARE_SCALING) {
            return 1;
        }

        const scaleZoom = this.scene.scale.zoom || 1;
        const effectiveZoom = Math.max(cameraZoom * scaleZoom, 0.0001);
        // Preserve default behavior when zoomed in: labels scale with the world (text scale stays at 1).
        if (effectiveZoom >= 1 && effectiveZoom <= NAME_LABEL_ZOOM_IN_SHRINK_START) {
            return 1;
        }

        let worldScale: number;
        if (effectiveZoom < 1) {
            const t = Phaser.Math.Clamp(normalizedDiscreteLevel, 0, 1);
            const easedT = Math.pow(t, NAME_LABEL_SCALE_ZOOM_EXPONENT);
            const zoomOutBoost = 1 - easedT; // 1 at max zoom-out, 0 near baseline zoom
            const targetScreenScale = Phaser.Math.Linear(
                NAME_LABEL_SCREEN_SCALE_BASE,
                NAME_LABEL_SCREEN_SCALE_MAX,
                zoomOutBoost
            );
            worldScale = targetScreenScale / effectiveZoom;
        } else {
            const shrinkT = Phaser.Math.Clamp(
                (effectiveZoom - NAME_LABEL_ZOOM_IN_SHRINK_START) / NAME_LABEL_ZOOM_IN_SHRINK_RANGE,
                0,
                1
            );
            const easedShrinkT = Math.pow(shrinkT, NAME_LABEL_ZOOM_IN_SHRINK_EXPONENT);
            worldScale = Phaser.Math.Linear(1, NAME_LABEL_ZOOM_IN_WORLD_SCALE_MIN, easedShrinkT);
        }
        const clamped = Phaser.Math.Clamp(worldScale, NAME_LABEL_SCALE_MIN, NAME_LABEL_SCALE_MAX);
        const quantized = Math.round(clamped / NAME_LABEL_SCALE_STEP) * NAME_LABEL_SCALE_STEP;
        return Phaser.Math.Clamp(quantized, NAME_LABEL_SCALE_MIN, NAME_LABEL_SCALE_MAX);
    }

    private applyPlayerNameScaleForCurrentSceneZoom(): void {
        const cameraZoom = this.scene.cameras.main?.zoom || 1;
        const cameraManager =
            typeof this.scene.getCameraManager === "function" ? this.scene.getCameraManager() : undefined;

        if (cameraManager && typeof cameraManager.captureZoomStateSnapshot === "function") {
            const { normalizedDiscreteLevel } = cameraManager.captureZoomStateSnapshot();
            this.updatePlayerNameScaleForDiscreteLevel(cameraZoom, normalizedDiscreteLevel);
            return;
        }

        // Fallback during early init before CameraManager is ready.
        this.applyPlayerNameVisualScale(this.computePlayerNameVisualScale(cameraZoom));
    }

    private applyPlayerNameVisualScale(nextScale: number): void {
        if (!Number.isFinite(nextScale) || nextScale <= 0) {
            return;
        }

        if (Math.abs(this.playerNameVisualScale - nextScale) <= 0.0001) {
            return;
        }

        this.playerNameVisualScale = nextScale;

        if (!this.playerNameText) {
            return;
        }

        this.playerNameText.setScale(nextScale);
        this.refreshPlayerNameAccessoriesPosition();
    }

    private refreshPlayerNameAccessoriesPosition(): void {
        if (!this.playerNameText) {
            return;
        }
        this.statusDot.x = (this.playerNameText.getLeftCenter().x ?? 0) - 6;
        this.megaphoneIcon.setX((this.playerNameText.getRightCenter().x ?? 0) + 8);
    }

    protected playAnimation(direction: PositionMessage_Direction, moving: boolean): void {
        if (this.invisible) return;
        for (const [texture, sprite] of this.sprites.entries()) {
            if (!sprite.anims) {
                console.error("ANIMS IS NOT DEFINED!!!");
                return;
            }
            const directionStr = ProtobufClientUtils.toDirectionString(direction);
            if (moving && (!sprite.anims.currentAnim || sprite.anims.currentAnim.key !== directionStr)) {
                sprite.play(texture + "-" + directionStr + "-" + PlayerAnimationTypes.Walk, true);
            } else if (!moving) {
                sprite.anims.play(texture + "-" + directionStr + "-" + PlayerAnimationTypes.Idle, true);
            }
        }
    }

    protected getBody(): Phaser.Physics.Arcade.Body {
        const body = this.body;
        if (!(body instanceof Phaser.Physics.Arcade.Body)) {
            throw new Error("Container does not have arcade body");
        }
        return body;
    }

    stop() {
        this.getBody().setVelocity(0, 0);
        this.playAnimation(this._lastDirection, false);
    }

    say(text: string, type: SayMessageType) {
        this.scene.markDirty();
        if (this.bubble !== null) {
            this.remove(this.bubble);
            this.bubble.destroy();
        }
        // If text is empty, let's just remove the previous bubble and say nothing.
        if (!text) {
            return;
        }

        switch (type) {
            case SayMessageType.SpeechBubble:
            case SayMessageType.UNRECOGNIZED: {
                const speechBubble = new SpeechBubble(text);
                this.bubble = new DOMElement(
                    this.scene,
                    0,
                    0 - CHARACTER_BODY_HEIGHT / 2 - 50,
                    speechBubble.getElement()
                );
                this.add(this.bubble);
                break;
            }
            case SayMessageType.ThinkingCloud: {
                const thinkElement = new ThinkingCloud({
                    text: text,
                    maxWidth: 200,
                    fontSize: 11,
                    cornerRadius: 10,
                    padding: 12,
                    fillColor: 0xffffff,
                    fillAlpha: 0.8,
                }).getElement();
                this.bubble = new DOMElement(this.scene, 0, 0 - CHARACTER_BODY_HEIGHT / 2 - 70, thinkElement);
                this.add(this.bubble);
                break;
            }
            default: {
                const _exhaustiveCheck: never = type;
            }
        }
    }

    destroy(): void {
        if (this.companionPictureUnsubscribe) {
            this.companionPictureUnsubscribe();
            this.companionPictureUnsubscribe = undefined;
        }
        if (this.isCurrentPlayer) {
            currentPlayerCompanionStore.set(undefined);
        }
        for (const sprite of this.sprites.values()) {
            if (this.scene) {
                this.scene.sys.updateList.remove(sprite);
            }
        }
        this.texturePromise?.cancel();
        this.list.forEach((objectContaining) => objectContaining.destroy());
        this.outlineColorStoreUnsubscribe?.();
        this.destroyed = true;
        super.destroy();
    }

    playEmote(emote: string) {
        this.cancelPreviousEmote();
        const emoteY = -45;
        const span = document.createElement("span");
        span.innerHTML = emote;
        this.emote = new DOMElement(this.scene, -1, 0, span, "z-index:10;");
        this.emote.setAlpha(0);
        this.add(this.emote);
        this.createStartTransition(emoteY);
    }

    playText(
        id: string,
        text: string,
        duration = 10000,
        callback = () => this.destroyText(id),
        createStackAnimation = true,
        type: "warning" | "message" = "message"
    ) {
        if (this.texts.has(id)) {
            this.destroyText(id);
        }
        this.textsToBuild.set(id, { text, duration, callback, type });

        // If there is already one text created, we don't need to create a stack animation
        if (this.texts.size == 1 && createStackAnimation) {
            this.createStackAnimationForMultiText();
            return;
        }

        const speechDomElement = new SpeechDomElement(
            id,
            text,
            this.scene,
            -1,
            -30 + this.texts.size * 2,
            callback,
            type
        );
        this.add(speechDomElement);
        this.texts.set(id, speechDomElement);
        speechDomElement.play(-1, -50 + this.texts.size * 2, duration, (id) => {
            this.destroyText(id);
        });
    }

    private createStartTransition(emoteY: number) {
        this.emoteTween = this.scene?.tweens.add({
            targets: this.emote,
            props: {
                alpha: 1,
                y: emoteY,
            },
            ease: "Power2",
            duration: 500,
            onComplete: () => {
                this.startPulseTransition(emoteY);
            },
        });
    }

    private startPulseTransition(emoteY: number) {
        if (this.emote) {
            this.emoteTween = this.scene?.tweens.add({
                targets: this.emote,
                props: {
                    y: emoteY * 1.3,
                    scale: this.emote.scale * 1.1,
                },
                duration: 250,
                yoyo: true,
                repeat: 1,
                completeDelay: 200,
                onComplete: () => {
                    this.startExitTransition(emoteY);
                },
            });
        }
    }

    private startExitTransition(emoteY: number) {
        this.emoteTween = this.scene?.tweens.add({
            targets: this.emote,
            props: {
                alpha: 0,
                y: 2 * emoteY,
            },
            ease: "Power2",
            duration: 500,
            onComplete: () => {
                this.destroyEmote();
            },
        });
    }

    private setOutline(color: number | undefined) {
        if (!this.playerNameText) {
            throw new Error("Player name text is not defined when setOuline is called");
        }
        if (color === undefined) {
            this.getOutlinePlugin()?.remove(this.playerNameText);
        } else {
            this.getOutlinePlugin()?.remove(this.playerNameText);
            this.getOutlinePlugin()?.add(this.playerNameText, {
                thickness: 2,
                outlineColor: color,
            });
        }

        //Using outline quickfix
        this.scene.refreshSceneForOutline();
    }

    private cancelPreviousEmote() {
        if (!this.emote) return;

        this.emoteTween?.remove();
        this.destroyEmote();
    }

    private destroyEmote() {
        this.emote?.destroy();
        this.emote = null;
    }

    destroyText(id: string) {
        const text = this.texts.get(id);
        text?.destroy();
        this.texts.delete(id);
        this.textsToBuild.delete(id);
    }

    destroyAllText() {
        for (const [id] of this.texts) {
            this.destroyText(id);
        }
    }

    private createStackAnimationForMultiText() {
        // Destroy all texts and recreate them
        this.texts.forEach((text, id) => {
            // Destroy and remove the text from the map
            text.destroy();
            this.texts.delete(id);
        });

        // Recreate all texts in the correct order (from the biggest length to the smallest)
        Array.from(this.textsToBuild.entries())
            .sort((a, b) => a[1].text.length - b[1].text.length)
            .forEach(([id, { text, duration, callback }]) => {
                this.playText(id, text, duration, callback, false);
            });
    }

    public get pictureStore(): PictureStore {
        return this._pictureStore;
    }

    public setFollowOutlineColor(color: number): void {
        this.outlineColorStore.setFollowColor(color);
    }

    public removeFollowOutlineColor(): void {
        this.outlineColorStore.removeFollowColor();
    }

    public setApiOutlineColor(color: number): void {
        this.outlineColorStore.setApiColor(color);
    }

    public removeApiOutlineColor(): void {
        this.outlineColorStore.removeApiColor();
    }

    public pointerOverOutline(color: number): void {
        this.outlineColorStore.pointerOver(color);
    }

    public pointerOutOutline(): void {
        this.outlineColorStore.pointerOut();
    }

    public characterCloseByOutline(color: number): void {
        this.outlineColorStore.characterCloseBy(color);
    }

    public characterFarAwayOutline(): void {
        this.outlineColorStore.characterFarAway();
    }

    private getCurrentOutline(): { thickness: number; color?: number } {
        return { thickness: 2, color: get(this.outlineColorStore) };
    }

    /**
     * Returns a promise that resolves as soon as a texture is displayed for the user.
     * The promise will return when the required texture is loaded OR when the fallback texture is loaded (in case
     * the required texture could not be loaded).
     */
    public getTextureLoadedPromise(): PromiseLike<void> {
        return this.textureLoadedDeferred.promise;
    }

    public get lastDirection(): PositionMessage_Direction {
        return this._lastDirection;
    }

    public handlePressSpacePlayerTextCallback() {
        for (const [, text] of this.texts) {
            (text as SpeechDomElement).callback();
        }
    }
}
